import dayjs from "dayjs";
import { FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useUpdateEffect } from "react-use";
import { MonthPicker, YearPicker } from "src/components";
import { getCurrentQuarter } from "src/features/observationsAdmin/utils";

export interface ExportOptionsFormState {
	exportType?: "Monthly" | "Quarterly" | "Yearly";

	quarter?: "Q1" | "Q2" | "Q3" | "Q4" | null;

	month?: string | null;

	year?: number | null;
}

export interface ExportOptionsFormProps {
	className?: string;
	years?: number[];
}

const ExportOptionsForm: FC<ExportOptionsFormProps> = ({ years }) => {
	const { control, setValue } = useFormContext<ExportOptionsFormState>();

	const { exportType } = useWatch({ control });

	useUpdateEffect(() => {
		const currentMonth = dayjs().format("MMMM");
		const currentYear = dayjs().year();

		setValue("year", currentYear);

		if (exportType === "Monthly") {
			setValue("month", currentMonth);
			setValue("quarter", null);
		}

		if (exportType === "Quarterly") {
			setValue("month", null);
			setValue("quarter", getCurrentQuarter(dayjs()));
		}

		if (exportType === "Yearly") {
			setValue("month", null);
			setValue("quarter", null);
		}
	}, [exportType]);

	return (
		<form>
			<fieldset>
				<Controller
					name="exportType"
					control={control}
					render={({ field: { onChange, value } }) => (
						<div className="flex items-center gap-6">
							<legend className="text-sm font-medium text-zinc-600 m-0">
								Export:
							</legend>

							{(["Monthly", "Quarterly", "Yearly"] as const).map((opt) => (
								<label
									key={opt}
									className="flex items-center gap-2 cursor-pointer text-sm text-zinc-700"
								>
									<input
										type="radio"
										value={opt}
										checked={value === opt}
										onChange={() => onChange(opt)}
										className="accent-primary"
									/>
									{opt}
								</label>
							))}
						</div>
					)}
				/>
			</fieldset>

			<hr className="border-border my-4" />

			<div className="flex gap-4">
				{exportType === "Quarterly" && (
					<div>
						<div className="flex items-center gap-2">
							<label
								htmlFor="quarter-select"
								className="text-sm font-medium text-zinc-600 m-0"
							>
								Quarter:
							</label>

							<Controller
								name="quarter"
								control={control}
								render={({ field }) => (
									<select
										{...field}
										id="quarter-select"
										value={field.value ?? ""}
										className="rounded-lg border border-border bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
									>
										<option>Q1</option>
										<option>Q2</option>
										<option>Q3</option>
										<option>Q4</option>
									</select>
								)}
							/>
						</div>
					</div>
				)}

				{exportType === "Monthly" && (
					<MonthPicker name="month" control={control} />
				)}

				<YearPicker
					name="year"
					years={years}
					control={control}
					width={exportType === "Monthly" ? "220px" : "100%"}
				/>
			</div>
		</form>
	);
};

export default ExportOptionsForm;
